import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, Users2, Trophy, BookOpen, Wand2, ArrowRight, ShieldCheck, Zap } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { LogoIcon } from "@/components/Logo";

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();
  useEffect(() => { if (user) nav("/panel"); }, [user]);

  return (
    <div className="min-h-screen auth-bg relative overflow-hidden">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-white/40 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <motion.span
              initial={{ rotate: -8, scale: 0.85, opacity: 0 }}
              animate={{ rotate: 0, scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 220, damping: 18, delay: 0.05 }}
              className="inline-flex"
            >
              <LogoIcon size={36} />
            </motion.span>
            <motion.span
              initial={{ x: -8, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="font-display font-bold text-xl tracking-tight"
            >
              Leranio
            </motion.span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/fiyatlandirma" data-testid="header-pricing-link"><Button variant="ghost" size="sm">Fiyatlandırma</Button></Link>
            <Link to="/giris" data-testid="header-login-link"><Button variant="ghost" size="sm">Giriş Yap</Button></Link>
            <Link to="/kayit" data-testid="header-register-link"><Button size="sm" className="rounded-full px-5">Ücretsiz Başla</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-16 md:pt-24 pb-20">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-6 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass border text-xs font-medium">
              <Sparkles size={12} style={{ color: "#7C4DFF" }} /> Learning Made Intelligent
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Öğrenmenin<br/>
              <span className="brand-text-gradient">yeni operasyon sistemi</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl leading-relaxed">
              Leranio — öğretmen, öğrenci, veli ve yöneticiler için hepsi tek ekranda. Ödev takibi, AI özel öğretmen (Sokratik yöntemle), otomatik ödev kontrolü, kişisel çalışma planları ve çok daha fazlası.
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Link to="/kayit" data-testid="hero-cta-primary">
                <Button size="lg" className="rounded-full px-6 h-12">Hemen Ücretsiz Dene <ArrowRight size={16} className="ml-1.5" /></Button>
              </Link>
              <Link to="/giris" data-testid="hero-cta-secondary">
                <Button size="lg" variant="outline" className="rounded-full px-6 h-12">Demo Hesaplarla Gir</Button>
              </Link>
            </div>
            <div className="flex flex-wrap gap-6 pt-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-emerald-500" /> KVKK & GDPR uyumlu</span>
              <span className="flex items-center gap-1.5"><Zap size={14} className="text-amber-500" /> Anlık bildirimler</span>
              <span className="flex items-center gap-1.5"><Trophy size={14} className="text-orange-500" /> Oyunlaştırma sistemi</span>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="grid grid-cols-2 gap-3 animate-fade-up" style={{ animationDelay: "150ms" }}>
              <HeroCard icon={BookOpen} label="Ödevler" tint="indigo" big="128" small="+12 bu hafta" />
              <HeroCard icon={Sparkles} label="AI Sohbet" tint="cyan" big="94" small="Sokratik yöntem" />
              <HeroCard icon={Trophy} label="XP Puanı" tint="orange" big="340" small="Seviye 4" />
              <HeroCard icon={Users2} label="Sınıf" tint="emerald" big="28" small="Aktif öğrenci" />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="text-center mb-10">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Her rol için özenle tasarlandı</h2>
          <p className="mt-2 text-muted-foreground">Öğretmen, öğrenci, veli ve yönetici &mdash; herkes için özel deneyim.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Feature icon={Sparkles} title="AI Sokratik Öğretmen" desc="Cevabı vermez, sorularla öğrenciyi düşünmeye yönlendirir." tint="cyan" />
          <Feature icon={BookOpen} title="Akıllı Ödev Kontrol" desc="Defter fotoğrafı yükle; AI eksik ve yanlışları tespit etsin." tint="indigo" />
          <Feature icon={Wand2} title="Kişisel Çalışma Koçu" desc="Zayıf konulara göre haftalık plan otomatik oluşur." tint="emerald" />
          <Feature icon={Trophy} title="Motive Eden Oyunlaştırma" desc="XP, rozet ve liderlik tablosu ile öğrenmeyi eğlenceli hale getir." tint="orange" />
        </div>
      </section>

      <footer className="border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
          <div>© {new Date().getFullYear()} Leranio. Tüm hakları saklıdır.</div>
          <div>Learning Made Intelligent · Türk müfredatı için tasarlandı.</div>
        </div>
      </footer>
    </div>
  );
}

function HeroCard({ icon: Icon, label, tint, big, small }) {
  return (
    <div className="glass rounded-2xl p-5 border ambient-shadow">
      <div className={`w-9 h-9 rounded-lg bg-${tint}-500/15 text-${tint}-600 grid place-items-center mb-3`}>
        <Icon size={18} />
      </div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="font-display text-3xl font-bold mt-1">{big}</div>
      <div className="text-xs text-muted-foreground mt-1">{small}</div>
    </div>
  );
}

function Feature({ icon: Icon, title, desc, tint }) {
  return (
    <div className="p-5 rounded-2xl border border-border bg-card/60 hover:bg-card transition-transform duration-200 ease-out hover:-translate-y-1">
      <div className={`w-10 h-10 rounded-lg bg-${tint}-500/15 text-${tint}-600 grid place-items-center mb-3`}>
        <Icon size={20} />
      </div>
      <h3 className="font-display font-semibold text-lg mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
    </div>
  );
}
